// src/features/license/EntitlementsEditor.tsx
// Editor for LicenseEntitlements — per-product entitlements (enabled, plan,
// limits) and global feature flags. Used by both IssueLicenseDialog and the
// "edit entitlements" dialog in the detail rail.

import { useI18n } from '@react/hooks';
import { Button, Badge } from '@react/components';
import type { LicenseEntitlements, LicenseFeatureFlag, LicenseProductEntitlement } from '@core/types/license';

interface EntitlementsEditorProps {
  value: LicenseEntitlements;
  onChange: (next: LicenseEntitlements) => void;
}

function emptyProduct(): LicenseProductEntitlement {
  return { product_slug: '', product_name: '', is_enabled: true, plan_slug: '', features: [], limits: { max_users: 0, max_storage_bytes: 0, max_projects: 0, max_api_calls_per_day: 0 } };
}

function emptyFlag(): LicenseFeatureFlag {
  return { key: '', name: '', is_enabled: true };
}

function fieldClass(extra = '') {
  return `rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring ${extra}`;
}

export function EntitlementsEditor({ value, onChange }: EntitlementsEditorProps) {
  const { t } = useI18n();
  const products = value.products ?? [];
  const features = value.features ?? [];

  const updateProduct = (i: number, patch: Partial<LicenseProductEntitlement>) => {
    const next = products.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ ...value, products: next });
  };

  const updateLimits = (i: number, patch: Partial<NonNullable<LicenseProductEntitlement['limits']>>) => {
    const current = products[i].limits ?? { max_users: 0, max_storage_bytes: 0, max_projects: 0, max_api_calls_per_day: 0 };
    updateProduct(i, { limits: { ...current, ...patch } });
  };

  const removeProduct = (i: number) => onChange({ ...value, products: products.filter((_, idx) => idx !== i) });
  const addProduct = () => onChange({ ...value, products: [...products, emptyProduct()] });

  const updateFlag = (i: number, patch: Partial<LicenseFeatureFlag>) => {
    const next = features.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ ...value, features: next });
  };
  const removeFlag = (i: number) => onChange({ ...value, features: features.filter((_, idx) => idx !== i) });
  const addFlag = () => onChange({ ...value, features: [...features, emptyFlag()] });

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-foreground">{t('license.entitlements.products')}</h4>
          <Button size="sm" variant="secondary" onClick={addProduct}>{t('license.entitlements.addProduct')}</Button>
        </div>
        <div className="mt-2 space-y-2">
          {products.map((p, i) => (
            <div key={i} className="space-y-2 rounded-md border border-border p-2">
              <div className="flex items-center gap-2">
                <input value={p.product_slug} onChange={(e) => updateProduct(i, { product_slug: e.target.value })} placeholder={t('license.entitlements.productSlug')} className={fieldClass('flex-1 font-mono')} />
                <input value={p.plan_slug} onChange={(e) => updateProduct(i, { plan_slug: e.target.value })} placeholder={t('license.entitlements.planSlug')} className={fieldClass('flex-1')} />
                <button
                  type="button"
                  role="switch"
                  aria-checked={p.is_enabled}
                  aria-label={t('common.active')}
                  onClick={() => updateProduct(i, { is_enabled: !p.is_enabled })}
                  className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${p.is_enabled ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-sm transition-transform ${p.is_enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
                <button onClick={() => removeProduct(i)} aria-label={t('common.remove')} className="text-muted-foreground hover:text-destructive">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <label className="flex items-center justify-between gap-1 rounded-md bg-muted px-2 py-1">
                  <span className="text-[11px] text-muted-foreground">{t('license.entitlements.maxUsers')}</span>
                  <input type="text" inputMode="numeric" value={p.limits?.max_users ?? 0} onChange={(e) => updateLimits(i, { max_users: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })} className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none" />
                </label>
                <label className="flex items-center justify-between gap-1 rounded-md bg-muted px-2 py-1">
                  <span className="text-[11px] text-muted-foreground">{t('license.entitlements.maxProjects')}</span>
                  <input type="text" inputMode="numeric" value={p.limits?.max_projects ?? 0} onChange={(e) => updateLimits(i, { max_projects: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })} className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none" />
                </label>
                <label className="flex items-center justify-between gap-1 rounded-md bg-muted px-2 py-1">
                  <span className="text-[11px] text-muted-foreground">{t('license.entitlements.maxApiCalls')}</span>
                  <input type="text" inputMode="numeric" value={p.limits?.max_api_calls_per_day ?? 0} onChange={(e) => updateLimits(i, { max_api_calls_per_day: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })} className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none" />
                </label>
                <label className="flex items-center justify-between gap-1 rounded-md bg-muted px-2 py-1">
                  <span className="text-[11px] text-muted-foreground">{t('license.entitlements.maxStorageMb')}</span>
                  <input type="text" inputMode="numeric" value={Math.round((p.limits?.max_storage_bytes ?? 0) / (1024 * 1024))} onChange={(e) => updateLimits(i, { max_storage_bytes: (Number(e.target.value.replace(/[^0-9]/g, '')) || 0) * 1024 * 1024 })} className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none" />
                </label>
              </div>
            </div>
          ))}
          {products.length === 0 && <p className="text-xs text-muted-foreground">{t('license.entitlements.noProducts')}</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-foreground">{t('license.entitlements.features')}</h4>
          <Button size="sm" variant="secondary" onClick={addFlag}>{t('license.entitlements.addFeature')}</Button>
        </div>
        <div className="mt-2 space-y-1.5">
          {features.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={f.key} onChange={(e) => updateFlag(i, { key: e.target.value })} placeholder={t('license.entitlements.featureKey')} className={fieldClass('flex-1 font-mono')} />
              <input value={f.name} onChange={(e) => updateFlag(i, { name: e.target.value })} placeholder={t('common.name')} className={fieldClass('flex-1')} />
              <button
                type="button"
                onClick={() => updateFlag(i, { is_enabled: !f.is_enabled })}
                className="shrink-0"
                aria-label={t('common.active')}
              >
                <Badge variant={f.is_enabled ? 'success' : 'default'}>{f.is_enabled ? t('common.active') : t('common.inactive')}</Badge>
              </button>
              <button onClick={() => removeFlag(i)} aria-label={t('common.remove')} className="text-muted-foreground hover:text-destructive">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </div>
          ))}
          {features.length === 0 && <p className="text-xs text-muted-foreground">{t('license.entitlements.noFeatures')}</p>}
        </div>
      </div>
    </div>
  );
}
